"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Lock, ArrowRight, TrendingUp, CircleAlert as AlertCircle, ChartBar as BarChart3, CircleCheck as CheckCircle2, SlidersHorizontal, Calendar, Layers, ArrowUpRight, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function DashboardPreviewHero() {
  return <section id="dashboardpreviewhero" data-nav="dark" className="dark relative overflow-hidden bg-background text-foreground py-20 lg:py-28">
      {/* Subtle atmospheric glow and architectural grid texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[48rem] rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-secondary/10 blur-2xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)/0.15_1px,transparent_1px),linear-gradient(to_bottom,var(--border)/0.15_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,#000_70%,transparent_100%)] opacity-30" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center space-y-6">
          <div className="flex justify-center">
            <Badge variant="outline" className="border-secondary/40 bg-secondary/10 text-secondary-foreground font-sans px-3.5 py-1 text-xs sm:text-sm tracking-wide">
              <Lock className="size-3.5 text-secondary mr-1.5 shrink-0" />
              Illustrative preview — not a live login
            </Badge>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
            See what your automated hotel dashboard looks like
          </h1>

          <p className="font-sans text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Continuous rate tracking across primary booking channels, live OTA parity monitoring, and algorithmic demand forecasting designed for independent and luxury properties.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90 transition-colors shadow-sm">
              <Link to="#">
                Book a consultation
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-card/60 text-foreground hover:bg-card hover:text-foreground transition-colors">
              <Link to="#">
                Explore revenue services
              </Link>
            </Button>
          </div>
        </div>

        {/* Dashboard Interface Mockup */}
        <div className="mt-14 lg:mt-18" aria-hidden="true">
          <div className="rounded-xl border border-border/70 bg-card/90 shadow-2xl backdrop-blur-sm p-4 sm:p-6 lg:p-8">
            
            {/* Mockup Top Navigation Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border/80 pb-5">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-full bg-destructive/60" />
                  <div className="size-3 rounded-full bg-secondary/60" />
                  <div className="size-3 rounded-full bg-chart-2/60" />
                </div>
                <span className="text-xs font-mono tracking-wider text-muted-foreground uppercase pl-2 border-l border-border">
                  HOTELREVBABA ANALYTICS ENGINE v4.2
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-mono bg-accent/20 border-secondary/30 text-secondary">
                  <ShieldCheck className="size-3 mr-1" />
                  PARITY AUDIT: 99.4%
                </Badge>
                <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 px-2.5 py-1 rounded border border-border">
                  <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                  Next 30 Days Forecast
                </div>
              </div>
            </div>

            {/* Quick KPI Stat Panels */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <Card className="bg-background/80 border-border p-4 gap-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-medium">
                  <span>Projected RevPAR</span>
                  <TrendingUp className="size-3.5 text-secondary" />
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-2xl font-bold font-serif text-foreground">$184.50</span>
                  <span className="text-xs font-mono font-medium text-emerald-400 flex items-center">
                    +18.2% <ArrowUpRight className="size-3" />
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">vs. CompSet Market Avg ($156)</p>
              </Card>

              <Card className="bg-background/80 border-border p-4 gap-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-medium">
                  <span>Average Daily Rate (ADR)</span>
                  <BarChart3 className="size-3.5 text-secondary" />
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-2xl font-bold font-serif text-foreground">$242.00</span>
                  <span className="text-xs font-mono font-medium text-emerald-400 flex items-center">
                    +12.4% <ArrowUpRight className="size-3" />
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Dynamic yield algorithm active</p>
              </Card>

              <Card className="bg-background/80 border-border p-4 gap-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-medium">
                  <span>OTA Rate Parity Status</span>
                  <CheckCircle2 className="size-3.5 text-emerald-400" />
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-2xl font-bold font-serif text-foreground">In Parity</span>
                  <span className="text-xs font-mono font-medium text-emerald-400">0 Violations</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Booking, Agoda, Expedia sync</p>
              </Card>

              <Card className="bg-background/80 border-border p-4 gap-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-medium">
                  <span>30-Day Demand Index</span>
                  <Layers className="size-3.5 text-secondary" />
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-2xl font-bold font-serif text-foreground">High Surge</span>
                  <span className="text-xs font-mono font-medium text-secondary">Index 92/100</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Local high-demand events detected</p>
              </Card>
            </div>

            {/* Visual Analytics & Feed Simulation */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
              
              {/* Main Chart Area */}
              <div className="lg:col-span-8 rounded-lg border border-border bg-background/60 p-5 flex flex-col justify-between">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground font-serif">Dynamic Rate Recommendation vs. CompSet</h2>
                    <p className="text-xs text-muted-foreground">Automated rate suggestions updated continuously</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <span className="size-2 rounded-full bg-secondary" /> Recommended Rate
                    </span>
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <span className="size-2 rounded-full bg-muted-foreground/50" /> Market Average
                    </span>
                  </div>
                </div>

                {/* Simulated Rate Graph Bars */}
                <div className="grid grid-cols-7 sm:grid-cols-14 gap-2 items-end h-40 pt-6 px-1">
                  {[{
                  day: "01",
                  val: 65,
                  comp: 50
                }, {
                  day: "03",
                  val: 72,
                  comp: 52
                }, {
                  day: "05",
                  val: 88,
                  comp: 58
                }, {
                  day: "07",
                  val: 95,
                  comp: 65
                }, {
                  day: "09",
                  val: 60,
                  comp: 48
                }, {
                  day: "11",
                  val: 70,
                  comp: 54
                }, {
                  day: "13",
                  val: 75,
                  comp: 58
                }, {
                  day: "15",
                  val: 90,
                  comp: 62
                }, {
                  day: "17",
                  val: 100,
                  comp: 70
                }, {
                  day: "19",
                  val: 82,
                  comp: 60
                }, {
                  day: "21",
                  val: 68,
                  comp: 52
                }, {
                  day: "23",
                  val: 85,
                  comp: 64
                }, {
                  day: "25",
                  val: 92,
                  comp: 68
                }, {
                  day: "27",
                  val: 98,
                  comp: 72
                }].map((bar, i) => <div key={bar.day} data-index={i} className="flex flex-col items-center gap-1.5 h-full justify-end group">
                      <div className="w-full flex items-end justify-center gap-0.5 h-32">
                        <div className="w-2.5 sm:w-3.5 bg-secondary rounded-t-sm transition-all group-hover:brightness-110" style={{
                      height: `${bar.val}%`
                    }} />
                        <div className="w-2.5 sm:w-3.5 bg-muted-foreground/20 rounded-t-sm" style={{
                      height: `${bar.comp}%`
                    }} />
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground">{bar.day}</span>
                    </div>)}
                </div>
              </div>

              {/* Parity & Channel Live Monitor */}
              <div className="lg:col-span-4 rounded-lg border border-border bg-background/60 p-5 flex flex-col justify-between space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-border/60">
                  <h3 className="text-sm font-semibold text-foreground font-serif">Live Channel Monitoring</h3>
                  <SlidersHorizontal className="size-3.5 text-muted-foreground" />
                </div>

                <div className="space-y-3 text-xs">
                  <div className="flex items-center justify-between p-2.5 rounded bg-card/60 border border-border/60">
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-emerald-400" />
                      <span className="font-medium text-foreground">Direct Web Engine</span>
                    </div>
                    <span className="font-mono text-secondary font-semibold">$242 / Best Rate</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded bg-card/60 border border-border/60">
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-emerald-400" />
                      <span className="font-medium text-foreground">Booking.com</span>
                    </div>
                    <span className="font-mono text-muted-foreground">$242 / Match</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded bg-card/60 border border-border/60">
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-emerald-400" />
                      <span className="font-medium text-foreground">Expedia Partner</span>
                    </div>
                    <span className="font-mono text-muted-foreground">$242 / Match</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded bg-card/60 border border-border/60">
                    <div className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-emerald-400" />
                      <span className="font-medium text-foreground">Agoda YCS</span>
                    </div>
                    <span className="font-mono text-muted-foreground">$242 / Match</span>
                  </div>
                </div>

                <div className="pt-2 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <AlertCircle className="size-3 text-secondary" />
                    Channel cache sync: 42s ago
                  </span>
                  <span className="font-mono text-secondary">Auto-Reprice ON</span>
                </div>
              </div>

            </div>

          </div>
        </div>

      </div>
    </section>;
}